'use client';

import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Alert from '@mui/material/Alert';
import { Typography } from '@mui/material';

import axios from 'axios';

import { ModuleRegistry } from 'ag-grid-community';
import { ColumnAutoSizeModule } from 'ag-grid-community';

ModuleRegistry.registerModules([ColumnAutoSizeModule]);

const AddNewContact = ({ vendor }) => {
  const [rowData, setRowData] = useState([]);
  const [open, setOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    headquarters: '',
    azureId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    onConfirm: null,
  });

  const [error, setError] = useState(null);
  const gridRef = useRef();

  const columnDefs = [
    {
      headerName: 'Actions',
      field: 'actions',
      width: 140,
      cellRenderer: (params) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <IconButton onClick={() => handleEdit(params.data)} color="primary" size="small" aria-label="edit">
            <EditIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={() => handleDeleteRequest(params.data.contactId)}
            color="error"
            size="small"
            aria-label="delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
    { field: 'firstName', headerName: 'First Name', width: 150 },
    { field: 'lastName', headerName: 'Last Name', width: 150 },
    { field: 'phone', headerName: 'Phone', width: 150 },

    { field: 'email', headerName: 'Email', width: 250 },

    { field: 'headquarters', headerName: 'Headquarters', width: 200 },
    {
      headerName: 'External App Access',
      field: 'azureId',
      editable: false,
      cellRenderer: (params) => {
        return params.value ? 'Yes' : 'No';
      },
    },
    { field: 'lastModifiedDate', headerName: 'Entry Time', width: 200 },
  ];

  const defaultColDef = { resizable: true, sortable: true, filter: true, flex: 1, cellStyle: { textAlign: 'left' } };

  useEffect(() => {
    if (vendor?.vendorId) {
      fetchContacts();
    }
  }, [vendor?.vendorId]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`/api/VendorContacts/get-contacts-by-vendorid/${vendor.vendorId}`);
      setRowData(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Failed to load contacts.');
    }
  };

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setNewContact({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      headquarters: '',
      azureId: '',
      contactId: null,
    });
  };

  const handleInputChange = (e) => setNewContact({ ...newContact, [e.target.name]: e.target.value });

  const handleEdit = (contact) => {
    setNewContact({
      email: contact.email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      phone: contact.phone,
      headquarters: contact.headquarters,
      azureId: contact.azureId,
      contactId: contact.contactId,
    });
    setOpen(true);
  };

  const handleDeleteRequest = (contactId) => {
    setConfirmDialog({
      open: true,
      title: 'Are you sure you want to delete this contact?',
      onConfirm: () => handleDelete(contactId),
    });
  };

  const handleDelete = async (contactId) => {
    try {
      const response = await axios.delete(`/api/VendorContacts/delete-contact-by-id/${contactId}`);
      if (response.status === 200 || response.status === 204) {
        fetchContacts();
        toast.success('Contact deleted successfully!');
      } else {
        setError('Failed to delete contact.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Error deleting contact.');
    }
  };

  const handleAddContact = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const vendorContactsResponse = await axios.get(`/api/VendorContacts`);
      const existingContact = vendorContactsResponse.data.find((c) => c.email === newContact.email);

      if (existingContact && existingContact.vendorName !== vendor.companyName) {
        return setConfirmDialog({
          open: true,
          title: `User already exists on vendor: ${existingContact.vendorName}. Do you want to switch to this vendor?`,
          onConfirm: async () => {
            await axios.delete(`/api/VendorContacts/delete-contact-by-id/${existingContact.contactId}`);
            processAddContact(); // <== call the main flow after confirm
          },
        });
      }

      await processAddContact(); // fall-through if no conflict
    } catch (error) {
      console.error('Error adding contact:', error);
      setError('Failed to add contact. Please try again.');
      setIsLoading(false);
      handleCloseDialog();
    }
  };

  const processAddContact = async () => {
    try {
      let contactId = newContact.contactId ?? null;
      if (!contactId) {
        const clientPayload = {
          vendorId: vendor.vendorId,
          nsVendorId: vendor.nsVendorId,
          vendorName: vendor.companyName,
        };
        const clientResponse = await axios.post(`/api/VendorContacts/add-contact`, clientPayload);
        if (clientResponse.status !== 200) throw new Error('Failed to create Client ID.');
        contactId = clientResponse.data.contactId;
      }

      let payload = {
        contactId,
        vendorId: vendor.vendorId,
        azureId: null,
        nsVendorId: vendor.nsVendorId,
        vendorName: vendor.companyName,
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        phone: newContact.phone,
        headquarters: newContact.headquarters,
        email: newContact.email,
      };

      const usersResponse = await axios.get(`/api/AzureB2c`);
      const existingUser = usersResponse.data.value.find((u) => u.displayName === newContact.email);

      if (!existingUser) {
        return setConfirmDialog({
          open: true,
          title: `${newContact.firstName} ${newContact.lastName} does not have external app access. Invite them?`,
          onConfirm: async () => {
            payload.mailNickname =
              `${newContact.firstName}.${newContact.lastName}`.toLowerCase().replace(/\s+/g, '').substring(0, 64) ||
              newContact.email.split('@')[0];

            payload.identities = [
              {
                signInType: 'emailAddress',
                issuer: 'wwm-llc.com',
                issuerAssignedId: newContact.email,
              },
            ];

            const inviteResponse = await axios.post(`/api/AzureB2c/InviteUser`, payload);
            if (inviteResponse.status === 200 || inviteResponse.status === 204) {
              payload.azureId = inviteResponse.data.id;
            } else {
              setError('Error inviting user.');
            }

            await axios.put(`/api/VendorContacts/update-contact`, payload);
            fetchContacts();
            toast.success('Contact saved successfully!');
          },
        });
      } else {
        payload.azureId = existingUser.id;
        await axios.put(`/api/AzureB2c/EditUser`, payload);
        await axios.put(`/api/VendorContacts/update-contact`, payload);
        fetchContacts();
        toast.success('Contact saved successfully!');
      }
    } catch (error) {
      console.error('Error processing contact:', error);
      setError('Failed to add contact. Please try again.');
    } finally {
      setIsLoading(false);
      handleCloseDialog();
    }
  };

  const ConfirmationDialog = () => (
    <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
      <DialogTitle>Confirm Action</DialogTitle>
      <DialogContent>
        <Typography>{confirmDialog.title}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
        <Button
          onClick={() => {
            setConfirmDialog({ ...confirmDialog, open: false });
            confirmDialog.onConfirm();
          }}
          color="primary"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div>
      {/* Button Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', marginTop: '10px' }}>
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          Add New Contact
        </Button>
      </div>

      {/* Error Alert */}
      {error && <Alert severity="error">{error}</Alert>}

      {/* Table Section */}
      <div style={{ height: '40vh', width: '100%', marginTop: '10px' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          domLayout="normal"
          defaultColDef={defaultColDef}
          onGridReady={(params) => params.api.sizeColumnsToFit()}
        />
      </div>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{newContact.contactId ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            fullWidth
            margin="dense"
            name="email"
            value={newContact.email}
            onChange={handleInputChange}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <TextField
              label="First Name"
              fullWidth
              margin="dense"
              name="firstName"
              value={newContact.firstName}
              onChange={handleInputChange}
            />
            <TextField
              label="Last Name"
              fullWidth
              margin="dense"
              name="lastName"
              value={newContact.lastName}
              onChange={handleInputChange}
            />
          </div>
          <TextField
            label="Phone"
            fullWidth
            margin="dense"
            name="phone"
            value={newContact.phone}
            onChange={handleInputChange}
          />
          <TextField
            label="Headquarters"
            fullWidth
            margin="dense"
            name="headquarters"
            value={newContact.headquarters}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary" disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAddContact} color="primary" disabled={isLoading || !newContact.email.trim()}>
            {isLoading ? 'Processing...' : newContact.email ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog />
    </div>
  );
};

export default AddNewContact;
